import * as _ from 'lodash';

import { Component, OnInit, isDevMode, Inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '@wephone-core/service/authentication';
import { MessageService, NoWhitespaceValidator, Platform } from '@wephone-utils';
import { KeychainTouchId } from '@ionic-native/keychain-touch-id/ngx/';
import { FlexBaseComponent } from '@wephone-core-ui';
import { StorageService } from '@wephone-core/service/storage.service';
import { CrmType } from '@wephone-core/model/entity/enterprise';
import { AppConfigService } from 'src/app/wephone-common/service/appconfig.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FlexIvrSettings } from '@wephone-core/service/flexivr_settings';
import { ElectronService } from 'ngx-electron';
import { SigninService, EnterpriseInfoResp } from '@wephone-session/service/signin.service';
import { APP_BASE_HREF } from '@angular/common';
import { _ti, TranslationService } from '@wephone-translation';
import { BoDomain } from '@wephone-core/system';
import { NoHtmlAndCurlyBracesValidator } from '@wephone-utils/validator/no-html-and-curly-braces-validator';
import { XSSValidator } from '@wephone-utils/validator/xss-validator';
import { LoginErrorCause } from '@wephone-core/service/authentication.i';

type LoginOption = 'google' | CrmType;
@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss']
})
export class SigninComponent extends FlexBaseComponent implements OnInit {
  static oAuths: any = {};

  loginToken: string;
  returnUrl: string;
  form: FormGroup;
  linkSignup: string;
  enterpriseName: string;
  loginOptions: LoginOption[] = [];
  password: string;
  inputDisable = false;
  useTouchidLogin: boolean;
  hasTouchId = undefined;
  savePassword = true;
  isProjectSuperAdmin: boolean;
  isProjectAdmin: boolean;
  // configPath: string;
  isDevMode = isDevMode;
  hasLoggedOut: boolean;
  multipleDomains: boolean;
  signinStep = 1;
  errorMessage: string;
  domains: string[];
  hasPasswordManager = false;
  oAuthTypes: any[] = [];
  isElectron = false;
  mfaRequired: boolean;
  private loginClicked: boolean;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthenticationService,
    private readonly appConfig: AppConfigService, // Do not remove this line!
    private readonly settings: FlexIvrSettings,
    private readonly keychainTouchId: KeychainTouchId,
    private readonly messageService: MessageService,
    private readonly storage: StorageService,
    // private readonly crypto: CryptoService,
    private readonly deviceService: DeviceDetectorService,
    private readonly signinService: SigninService,
    private readonly translationService: TranslationService,
    // private readonly  configManager: ConfigManager,
    electron: ElectronService,
    @Inject(APP_BASE_HREF) private baseHref: string,
    plateform: Platform,
    // private windowService: WindowService
  ) {
    super();
    this.hasLoggedOut =
      authService.hasLoggedOut() || (electron.isElectronApp && window['WINDOW_RELOADED']);
    this.linkSignup = FlexIvrSettings.getInstance().getAbsoluteWSv2URL('/signup');
    this.hasPasswordManager = electron.isElectronApp;

    // Available supported OAuth listed here
    SigninComponent.oAuths[CrmType.HUBSPOT] = {
      uri: this.settings.getAbsoluteWSv2URL('/oauth/hubspot'),
      logo: 'hubspot-logo.png',
      name: 'hubspot'
    };
    this.isElectron = plateform.isElectronApp();
  }

  isPhoneApp(): boolean {
    return this.settings.isProjectPhone();
  }

  get username(): string {
    return this.signinService.currentUserName;
  }

  set username(val: string) {
    this.signinService.setCurrentUserName(val);
  }

  private setOAuthList(): void {
    this.oAuthTypes = [];
    // this.loggerService.log('Login options', this.loginOptions);
    if (this.isProjectAdmin && this.loginOptions.length) {
      for (const lo of this.loginOptions) {
        if (!SigninComponent.oAuths[lo]) {
          continue;
        }
        this.oAuthTypes.push(SigninComponent.oAuths[lo]);
      }
    }
  }

  async ngOnInit(): Promise<void> {
    this.isProjectSuperAdmin = this.settings.isProjectSuperAdmin();
    this.isProjectAdmin = this.settings.isProjectAdmin();
    this.setOAuthList();

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || null; //  || '/dashboard'
    const locale = this.route.snapshot.queryParams['locale'];

    if (locale) {
      this.translationService.setAppLanguage(locale);
    }

    let domain = this.signinService.currentDomain;
    if (!domain) {
      domain = await this.authService.getLastDomain();

      if ((this.isProjectAdmin || this.isProjectSuperAdmin) && domain) {
        this.signinService.setCurrentDomain(domain);

        const domainUrl: string = this.signinService.getDomainForUrl(domain);
        this.authService.gotoLoginPage(domainUrl);
        return;
      }
    }

    this.form = this.fb.group({
      username: ['', [Validators.compose([Validators.required]), NoWhitespaceValidator, NoHtmlAndCurlyBracesValidator(), XSSValidator.sanitize]],
      password: [''],
      mfa_code: [''],
      savePassword: [false]
    });

    this.loginToken = this.route.snapshot.queryParams['loginToken'];
    this.multipleDomains = this.route.snapshot.queryParams['domain'] ? true : false;

    await this.fillForm();
    await this.setEnterpriseInfo();
    this.setOAuthList();
    super.ngOnInit();
  }

  doOAuth(uri: string): void {
    window.location.href = uri;
  }

  get domainName(): string {
    return this.signinService.currentDomain || '';
  }

  async setEnterpriseInfo(): Promise<void> {
    if (this.domainName && this.domainName === BoDomain) {
      this.enterpriseName = 'Super Admin';
      return;
    }

    if (this.baseHref !== '/v3' && this.baseHref !== '/bo' && this.domainName) {
      try {
        const res: EnterpriseInfoResp = await this.signinService.getEnterpriseInfo(this.domainName);
        if (res && res.name) {
          this.enterpriseName = res.name;
          this.loginOptions = res.login_options;
          return;
        }
      } catch (e) {
        this.loggerService.error('Set enterprise info got error', e);
        // this.enterpriseName = this.domainName;
        this.errorMessage = _ti('enterprise.validator.not_found');
        await this.signinService.clearLocalCredential();
        this.authService.gotoLoginPage();
      }
    }
  }

  private async fillForm(): Promise<any> {
    const queryLogin = this.route.snapshot.queryParams['login'];
    const queryDomain = this.route.snapshot.queryParams['login_domain'];
    if (queryDomain) {
      this.signinService.setCurrentDomain(queryDomain);
    }

    if (queryLogin) {
      this.username = queryLogin;
      this.domains = await this.getAvailDomains();
    } else if (this.domainName) {
      this.username = await this.storage.getDomainTempData('user_name', this.domainName);
    }
    this.signinStep = this.username && this.domainName ? 2 : 1;

    if (this.username) {
      this.form.get('username').setValue(this.username);

      if (this.domainName) {
        this.form.get('password').setValidators([Validators.required]);

        if (this.mfaRequired) {
          this.form.get('mfa_code').setValidators([Validators.required]);
        }
      }
    }
  }

  async onSubmit(): Promise<any> {
    this.errorMessage = null; // Clear message

    this.loginClicked = true;

    if (this.signinStep === 1) {
      this.username = _.toLower(this.form.get('username').value.trim());
      if (this.username.indexOf('::') === 0) {
        this.username = this.username.substr(2);
        this.settings.root_url = 'https://admin.graam.com.geekic.com';
        // this.loggerService.log('Root URL is now set to ', this.settings.root_url);
      }
      this.signinService.clearDomainDict();
      const savedUsername: string = await this.storage.getDomainTempData('user_name');
      if ((!this.domainName || this.domainName && savedUsername !== this.username)) { // !this.isProjectSuperAdmin && 
        this.storage.setDomainTempData('user_name', this.username);
        await this.getDomains();
        if (_.isEmpty(this.domains)) {
          this.loggerService.error('No domains available');
          // this.domainNameNotFoundToast();
          this.usernameNotFoundToast();
          this.loginClicked = false;
          return;
        }
      }
      await this.checkSavedPassword();
      this.signinStep = 2;
      this.loginClicked = false;
      return;
    }

    if (this.signinStep === 2) {
      this.password = this.form.get('password').value;
      this.savePassword = this.form.get('savePassword').value;
      await this.login();
    }

    this.loginClicked = false;
  }

  canSubmit(): boolean {
    if (this.loginClicked) {
      return false;
    }

    if (this.signinStep === 1) {
      return true;
    }

    // return !!this.form.get('password').value && (
    //   !this.mfaRequired || this.mfaRequired && !!this.form.get('mfa_code').value
    // );
    return true;
  }

  async checkSavedPassword(): Promise<any> {
    this.keychainTouchId.isAvailable().then(value => {
      this.hasTouchId = value;
    });

    if (!this.username) {
      return;
    }

    if (this.domainName) {
      this.password = await this.storage.getPassword(this.username, this.domainName);
      this.form.controls['password'].setValue(this.password, { emitEvent: false });

      if (this.password) {
        this.form.controls['savePassword'].setValue(true);
      } else {
        this.form.controls['savePassword'].setValue(false);
      }

      if (this.password && !this.hasLoggedOut) {
        await this.login();
      } else if (this.deviceService.isMobile()) {
        if (this.hasTouchId) {
          const passwordKeyname = this.domainName + '_password';
          this.useTouchidLogin = await this.storage.getAppConfig<boolean>('use_touchid_login');
          const hasSavedPassword = await this.keychainTouchId.has(passwordKeyname);
          if (hasSavedPassword) {
            this.password = await this.keychainTouchId.verify(passwordKeyname, _ti('login.touch_id_scan_your_finger_to_continue'));
            if (this.password) {
              await this.loginTouchId();
              if (!this.authService.isAuthenticated()) {
                this.keychainTouchId.delete(passwordKeyname);
              }
            }
          }
        }
      }
    }
  }

  domainNameNotFoundToast(): void {
    this.errorMessage = _ti('enterprise.validator.not_found');
  }

  loginErrorToast(message: string = ''): void {
    if (message) {
      this.errorMessage = message;
      return;
    }

    this.errorMessage = this.isProjectSuperAdmin ? _ti('enterprise.validator.wrong_account') : _ti('enterprise.validator.wrong_password');
  }

  usernameNotFoundToast(): void {
    this.errorMessage = _ti('enterprise.validator.account_not_found');
  }

  private async getAvailDomains(): Promise<string[]> {
    const domainEnterprises = await this.signinService.getDomainDict();
    return domainEnterprises ? Object.keys(domainEnterprises) : [];
  }

  private async getDomains(): Promise<any> {
    try {
      this.domains = await this.getAvailDomains();
      // this.loggerService.log('Available domains', this.domains);
      if (!_.isEmpty(this.domains)) {
        // If enterprise has more than one domain, then go to enterprise select page
        if (this.domains.length > 1) {
          // If current domain is one of the available domains, then go to that domain
          if (this.domainName && _.includes(this.domains, this.domainName)) {
            this.signinService.gotoDomain(this.domainName);
            return;
          }

          // Go to enterprise select page
          this.goEnterpriseSelect();
          return;
        }

        // If enterprise has only one domain
        // If the project is Admin or SuperAdmin then go to that domain
        if (this.settings.isProjectAdmin() || this.settings.isProjectSuperAdmin()) {
          // await this.authService.setLastDomain(this.domains[0]);
          await this.signinService.gotoDomain(this.domains[0]);
          return;
        }

        // If the project is Phone, then set the domain and fill the form
        this.signinService.setCurrentDomain(this.domains[0]);
        await this.storage.setDomainTempData('user_name', this.username, this.domains[0]);
        await this.fillForm();
        await this.setEnterpriseInfo();
        await this.checkSavedPassword();
      } else {
        this.loggerService.error('No domain available');
      }
    } catch (error) {
      this.loggerService.error('Check domain error: ', error);
      this.usernameNotFoundToast();
    }
  }

  private async login(): Promise<any> {
    if (!this.domainName) { // && !this.isProjectSuperAdmin
      this.domainNameNotFoundToast();
      this.loginClicked = false;
      return;
    }

    if (this.isOnDesktop()) {
      if (this.savePassword) {
        this.storage.savePassword(this.username, this.domainName, this.password);
      } else {
        this.storage.deletePassword(this.username, this.domainName, this.password);
      }
    }

    try {
      const mfaCode: string = this.form.get('mfa_code').value || null;
      if (this.mfaRequired && !mfaCode) {
        this.form.get('mfa_code').setErrors({
          required: true
        });
      }

      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }

      const result: {
        logged_in: boolean;
        mfa_required: boolean;
      } = await this.authService.login(this.username, this.password, mfaCode, this.domainName, this.returnUrl);

      if (result.logged_in) {
        // this.loggerService.log('this.returnUrl', this.returnUrl);
        // Has already broadcasted on authentication service
        this.saveKeychainTouchid(this.domainName, this.password);
        this.password = '';
        return;
      } else if (result.mfa_required) {
        this.mfaRequired = true;
        this.loginClicked = false;

        if (mfaCode) {
          throw new Error(_ti('enterprise.validator.incorrect_mfa_code'));
          // this.loginErrorToast(_ti('enterprise.validator.incorrect_mfa_code'));
        }
      } else {
        throw new Error(_ti('enterprise.validator.wrong_permission'));
        // this.loginErrorToast(_ti('enterprise.validator.wrong_permission'));
      }
    } catch (e: any) {
      this.loggerService.error('Login ', e);
      this.handleLoginError(e);
    }
  }

  private handleLoginError(error: any): void {
    let msg: string;
    const cause = error && error.cause;
    if (cause) {
      switch (cause) {
        case LoginErrorCause.INVALID_PASSWORD:
          msg = this.isProjectSuperAdmin ? _ti('enterprise.validator.wrong_account') : _ti('enterprise.validator.wrong_password');
          break;
        case LoginErrorCause.ACCOUNT_NOT_FOUND:
          msg = _ti('login.message.account_not_found');
          break;
        case LoginErrorCause.ACCOUNT_LOCKED:
          msg = _ti('login.message.account_locked');
          break;
        default:
          msg = error.message;
          break;
      }
    }
    this.loginErrorToast(msg);
  }

  isOnDesktop(): boolean {
    return this.deviceService.isDesktop();
  }

  async loginTouchId(): Promise<any> {
    try {
      const resp: {
        logged_in: boolean;
        mfa_required: boolean;
      } = await this.authService.login(this.username, this.password, null, this.domainName, this.returnUrl);

      if (!resp) {
        this.loggerService.error('No response from login');
        return;
      }

      if (resp.logged_in) {
        this.messageService.broadcast('application:login:authenticated');
        this.password = '';
      } else if (resp.mfa_required) {
        throw new Error(_ti('enterprise.validator.incorrect_mfa_code'));
        // this.loginErrorToast(_ti('enterprise.validator.incorrect_mfa_code'));
      }
    } catch (e) {
      this.loggerService.error('Error when logging in using saved password', e);
      this.handleLoginError(e);
    }
  }

  goEnterpriseSelect(): void {
    this.router.navigate(['s/enterprise-select']);
  }

  private saveKeychainTouchid(domainName, password): void {
    if (!this.hasTouchId) {
      return;
    }

    if (this.useTouchidLogin) {
      this.keychainTouchId.save(domainName + '_password', password, false);
      return;
    }

    this.keychainTouchId.delete(domainName + '_password');
  }

  changeUseTouchidLogin(): void {
    this.storage.setAppConfig<boolean>('use_touchid_login', this.useTouchidLogin);
  }

  protected async resolveData(): Promise<void> {
    try {
      this.uiBlocker.block(`SigninComponent resolveData`);

      if (this.signinService.currentDomain) {
        this.authService.setEnterpriseDomain(this.signinService.currentDomain);
      }

      if (!this.loginToken) {
        await this.authService.loginCookie(this.returnUrl);
        if (!this.authService.isAuthenticated()) {
          await this.checkSavedPassword();
        }
      } else {
        // Call reload session to update login form csrf token
        await this.authService.reloadSession();
        await this.authService.loginToken(this.returnUrl);
      }

    } catch (error) {
      this.loggerService.error('resolveData error', error);
      this.errorMessage = error.message;
      throw error;
    } finally {
      this.uiBlocker.unblock(`SigninComponent resolveData`);
    }
  }

  backLogin(resetDomain = false): void {
    if (resetDomain) {
      this.signinService.setCurrentDomain(undefined);
      this.goEnterpriseSelect();
      return;
    }

    this.signinStep = 1;

    this.form.get('password').clearValidators();
    this.form.get('password').updateValueAndValidity();

    this.form.get('mfa_code').clearValidators();
    this.form.get('mfa_code').updateValueAndValidity();
  }

  signUp(): void {
    if (!this.isElectron) {
      window.location.href = this.linkSignup;
      return;
    }
    window.open('https://www.graam.com/sign-up', '_blank');
  }
}
